// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Defines the observable compatibility and identity rules for agent runs.
 *
 * Tables, dialogs, and launch hooks share these pure decisions so extension and
 * minimum-version constraints cannot disagree across entry points.
 */
import type { Agent } from '@/subsystems/agents'

import type { DocumentListItemWithPipelines } from '../models'

export function agentAcceptsDocument(
  agent: Agent,
  document: DocumentListItemWithPipelines,
): boolean {
  if (document.latestVersion < (agent.documentFilter?.minVersions ?? 1)) return false
  if (document.sourceProvider && document.recordType && document.artifactType) {
    const artifactType = document.artifactType
    const mapped = agent.documentMappings.some(mapping => (
      mapping.provider === document.sourceProvider
      && mapping.recordType === document.recordType
      && mapping.artifactTypes.includes(artifactType)
    ))
    if (!mapped) return false
  }
  const extensions = agent.documentFilter?.extensions ?? []
  if (extensions.length === 0) return true
  const filename = (document.latestFilename ?? document.name ?? '').toLowerCase()
  return extensions.some((extension) => filename.endsWith(extension.toLowerCase()))
}

export function agentAcceptsDocuments(
  agent: Agent,
  documents: DocumentListItemWithPipelines[],
): boolean {
  return documents.every((document) => agentAcceptsDocument(agent, document))
}
